// ===========================================
// Terms Management Page
// ===========================================
// CRUD interface for managing school terms

import { useState, useMemo } from 'react';
import {
  Box,
  TextField,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Stack,
  Button,
  Typography,
} from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import PageHeader from '../../components/common/PageHeader';
import DataTable, { Column } from '../../components/common/DataTable';
import FormModal from '../../components/common/FormModal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import {
  useTerms,
  useCreateTerm,
  useUpdateTerm,
  useDeleteTerm,
  useReopenTerm,
  useRelockTerm,
} from '../../hooks/useAdmin';
import { Term } from '../../api/admin.api';
import ReopenTermDialog from '../../components/admin/ReopenTermDialog';
import { usePermissions } from '../../hooks/usePermissions';
import { useSchoolTimezone } from '../../contexts/TenantContext';
import { formatUTCDate } from '../../utils/dateUtils';
import { reopenLastDayLabel } from '../../utils/termLock';
import { apiErrorMessage } from '../../utils/apiError';

const fmtDate = (value: unknown): string =>
  formatUTCDate(value as string, { day: '2-digit', month: '2-digit', year: 'numeric' }, 'en-AU');

// ===========================================
// COMPONENT
// ===========================================

export default function TermsPage() {
  const { data: terms, isLoading, error } = useTerms();
  const createMutation = useCreateTerm();
  const updateMutation = useUpdateTerm();
  const deleteMutation = useDeleteTerm();
  const reopenMutation = useReopenTerm();
  const relockMutation = useRelockTerm();
  const { isOwner } = usePermissions();
  const timeZone = useSchoolTimezone();
  // Reopen / Lock now are owner-only on the server (`ownerOnly`); the buttons mirror it, never replace it.

  const [reopenTarget, setReopenTarget] = useState<Term | null>(null);
  const [lockError, setLockError] = useState<string | null>(null);
  // Server refusals for the Add/Edit modal and the delete confirm (e.g. 409 TERM_WOULD_UNLOCK).
  const [formError, setFormError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Filter state
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [hidePastTerms, setHidePastTerms] = useState(false);

  // Form state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTerm, setEditingTerm] = useState<Term | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    startDate: '',
    endDate: '',
  });

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [termToDelete, setTermToDelete] = useState<Term | null>(null);

  // Get available years from terms data
  const availableYears = useMemo(() => {
    if (!terms || terms.length === 0) return [currentYear];

    const years = new Set<number>();
    terms.forEach((term) => {
      const startYear = new Date(term.startDate).getFullYear();
      const endYear = new Date(term.endDate).getFullYear();
      years.add(startYear);
      years.add(endYear);
    });

    // Always include current year
    years.add(currentYear);

    return Array.from(years).sort((a, b) => b - a); // Descending order
  }, [terms, currentYear]);

  // Filter and sort terms
  const filteredTerms = useMemo(() => {
    if (!terms) return [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return terms
      .filter((term) => {
        // Filter by year
        const termStartYear = new Date(term.startDate).getFullYear();
        const termEndYear = new Date(term.endDate).getFullYear();
        const matchesYear = termStartYear === selectedYear || termEndYear === selectedYear;

        if (!matchesYear) return false;

        // Filter out past terms if toggle is on
        if (hidePastTerms) {
          const endDate = new Date(term.endDate);
          endDate.setHours(0, 0, 0, 0);
          if (endDate < today) return false;
        }

        return true;
      })
      .sort((a, b) => {
        // Sort by start date (earliest first = Term 1 at top)
        return new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
      });
  }, [terms, selectedYear, hidePastTerms]);

  // "Editing" cell, entirely from the SERVER's `lock` (D13): status, `ended`, `nextTerm*`. The reason
  // is visible text (a caption), not tooltip-only, so keyboard and screen-reader users get it too.
  const renderEditing = (term: Term) => {
    const lock = term.lock;
    if (!lock) return null;
    if (lock.status === 'LOCKED') {
      const reopenEnded =
        lock.reopenedUntil && new Date(lock.reopenedUntil).getTime() <= Date.now()
          ? reopenLastDayLabel(lock.reopenedUntil, timeZone)
          : null;
      return (
        <Stack spacing={0.25}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip icon={<LockIcon />} label="Locked" size="small" />
            {isOwner && (
              <Button size="small" onClick={() => { setLockError(null); setReopenTarget(term); }}>
                Reopen
              </Button>
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {`Locked because ${lock.lockedByTermName ?? 'the next term'} started on ${
              lock.lockedSince ? fmtDate(lock.lockedSince) : ''
            }`.trim()}
          </Typography>
          {reopenEnded && (
            <Typography variant="caption" color="text.secondary">
              {`Reopen ended ${reopenEnded}`}
            </Typography>
          )}
        </Stack>
      );
    }
    if (lock.status === 'REOPENED') {
      return (
        <Stack spacing={0.25}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              label={`Reopened until end of ${
                lock.reopenedUntil ? reopenLastDayLabel(lock.reopenedUntil, timeZone) : ''
              }`}
              size="small"
              color="warning"
            />
            {isOwner && (
              <Button
                size="small"
                disabled={relockMutation.isPending}
                onClick={() => { setLockError(null); relockMutation.mutate(term.id, { onError: (e) => setLockError(apiErrorMessage(e, 'Could not lock the term.')) }); }}
              >
                Lock now
              </Button>
            )}
          </Stack>
          {(lock.reopenedByName || lock.reopenReason) && (
            <Typography variant="caption" color="text.secondary">
              {`Reopened${lock.reopenedByName ? ` by ${lock.reopenedByName}` : ''}${
                lock.reopenReason ? `: ${lock.reopenReason}` : ''
              }`}
            </Typography>
          )}
        </Stack>
      );
    }
    // OPEN
    if (!lock.ended) return <Typography variant="body2">Editable</Typography>;
    return (
      <Typography variant="body2">
        {lock.nextTermName && lock.nextTermStartDate
          ? `Ended � editable until ${lock.nextTermName} starts ${fmtDate(lock.nextTermStartDate)}`
          : 'Ended � editable (no next term yet)'}
      </Typography>
    );
  };

  const handleReopen = async (input: { reason: string; days: number }) => {
    if (!reopenTarget) return;
    try {
      await reopenMutation.mutateAsync({ id: reopenTarget.id, ...input });
      setReopenTarget(null);
    } catch (err) {
      setLockError(apiErrorMessage(err, 'Could not reopen the term.'));
    }
  };

  // Table columns
  const columns: Column<Term>[] = [
    {
      id: 'name',
      label: 'Name',
      minWidth: 150,
    },
    {
      id: 'startDate',
      label: 'Start Date',
      minWidth: 120,
      format: fmtDate,
    },
    {
      id: 'endDate',
      label: 'End Date',
      minWidth: 120,
      format: fmtDate,
    },
    {
      id: '_count.Lesson',
      label: 'Lessons',
      minWidth: 80,
      align: 'center',
      format: (value) => (value as number) ?? 0,
    },
    {
      id: 'lock',
      label: 'Editing',
      minWidth: 220,
      sortable: false,
      format: (_value, term) => renderEditing(term),
    },
    {
      id: 'isActive',
      label: 'Status',
      minWidth: 100,
      align: 'right',
      format: (value) =>
        value ? (
          <Chip label="Active" size="small" color="success" />
        ) : (
          <Chip label="Inactive" size="small" color="default" />
        ),
    },
  ];

  // Handlers
  const handleAdd = () => {
    setFormError(null);
    setEditingTerm(null);
    setFormData({ name: '', startDate: '', endDate: '' });
    setModalOpen(true);
  };

  const handleEdit = (term: Term) => {
    setFormError(null);
    setEditingTerm(term);
    setFormData({
      name: term.name,
      startDate: term.startDate.split('T')[0],
      endDate: term.endDate.split('T')[0],
    });
    setModalOpen(true);
  };

  const handleDelete = (term: Term) => {
    setDeleteError(null);
    setTermToDelete(term);
    setDeleteDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (editingTerm) {
        await updateMutation.mutateAsync({
          id: editingTerm.id,
          data: formData,
        });
      } else {
        await createMutation.mutateAsync(formData);
      }
      setModalOpen(false);
    } catch (err) {
      // Shown inside the modal: the server's sentence (e.g. TERM_WOULD_UNLOCK) is the whole point.
      setFormError(apiErrorMessage(err, 'Could not save the term.'));
    }
  };

  const handleConfirmDelete = async () => {
    if (termToDelete) {
      try {
        await deleteMutation.mutateAsync(termToDelete.id);
        setDeleteDialogOpen(false);
        setTermToDelete(null);
      } catch (err) {
        setDeleteError(apiErrorMessage(err, 'Could not delete the term.'));
      }
    }
  };

  const isFormLoading = createMutation.isPending || updateMutation.isPending;
  const isDeleteLoading = deleteMutation.isPending;

  return (
    <Box>
      <PageHeader
        title="Terms"
        subtitle="Manage school terms and academic periods"
        breadcrumbs={[
          { label: 'Admin', path: '/admin' },
          { label: 'Terms' },
        ]}
        actionLabel="Add Term"
        onAction={handleAdd}
      />

      {lockError && !reopenTarget && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLockError(null)}>
          {lockError}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load terms. Please try again.
        </Alert>
      )}

      {/* Filters */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ mb: 2 }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
      >
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel id="year-filter-label">Year</InputLabel>
          <Select
            labelId="year-filter-label"
            value={selectedYear}
            label="Year"
            onChange={(e) => setSelectedYear(e.target.value as number)}
          >
            {availableYears.map((year) => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControlLabel
          control={
            <Switch
              checked={hidePastTerms}
              onChange={(e) => setHidePastTerms(e.target.checked)}
              size="small"
            />
          }
          label="Hide past terms"
        />

        {filteredTerms.length !== (terms?.length ?? 0) && (
          <Chip
            label={`Showing ${filteredTerms.length} of ${terms?.length ?? 0} terms`}
            size="small"
            variant="outlined"
          />
        )}
      </Stack>

      <DataTable
        columns={columns}
        data={filteredTerms}
        loading={isLoading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        emptyMessage={
          terms && terms.length > 0
            ? 'No terms match the current filters.'
            : "No terms configured. Click 'Add Term' to create one."
        }
        searchPlaceholder="Search terms..."
      />

      {/* Add/Edit Modal */}
      <FormModal
        open={modalOpen}
        title={editingTerm ? 'Edit Term' : 'Add Term'}
        submitLabel={editingTerm ? 'Update' : 'Create'}
        loading={isFormLoading}
        onSubmit={handleSubmit}
        onClose={() => { setModalOpen(false); setFormError(null); }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {formError && <Alert severity="error">{formError}</Alert>}
          <TextField
            label="Term Name"
            placeholder="e.g., Term 1 2025"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            fullWidth
          />
          <TextField
            label="Start Date"
            type="date"
            value={formData.startDate}
            onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
            required
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="End Date"
            type="date"
            value={formData.endDate}
            onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
            required
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      </FormModal>

      <ReopenTermDialog
        open={!!reopenTarget}
        termName={reopenTarget?.name ?? ''}
        loading={reopenMutation.isPending}
        error={lockError}
        onSubmit={handleReopen}
        onClose={() => { setReopenTarget(null); setLockError(null); }}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Term"
        message={`Are you sure you want to delete "${termToDelete?.name}"? This action cannot be undone.${
          deleteError ? `

${deleteError}` : ''
        }`}
        confirmLabel="Delete"
        confirmColor="error"
        loading={isDeleteLoading}
        onConfirm={handleConfirmDelete}
        onCancel={() => { setDeleteDialogOpen(false); setDeleteError(null); }}
      />
    </Box>
  );
}
